import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  LinearProgress,
  Collapse
} from '@mui/material';
import {
  SmartToy as BotIcon,
  Person as PersonIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { io } from 'socket.io-client';

/**
 * Composant de chat en temps réel pour le dialogue multi-agents
 * Affiche les messages échangés entre le Planificateur et le Manager
 * 
 * @param {boolean} isActive - Contrôle la connexion WebSocket (connecté si true)
 * @param {boolean} isVisible - Contrôle l'affichage du chat (affiché si true)
 * @param {function} onComplete - Callback appelé quand le dialogue est terminé
 */
const AgentDialogueChat = ({ isActive, isVisible = true, onComplete }) => {
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [currentTentative, setCurrentTentative] = useState(0);
  const [finalVerdict, setFinalVerdict] = useState(null); // Verdict final du Manager IA
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll vers le bas à chaque nouveau message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // ✅ TOUJOURS connecter le WebSocket dès le montage (sans attendre isActive)
    // Connexion WebSocket (TOUJOURS utiliser l'URL de production)
    // Ne PAS utiliser REACT_APP_API_URL car il pointe vers localhost en dev
    const apiUrl = 'https://api.astreintes.provserver.ovh';
    console.log('🔌 Tentative de connexion WebSocket à:', apiUrl);
    
    const socket = io(apiUrl, {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      secure: true, // Utiliser WSS (WebSocket Secure)
      rejectUnauthorized: false // Pour les certificats auto-signés si nécessaire
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('🔌 WebSocket connecté');
      setIsConnected(true);
      if (isActive) {
        addMessage({
          type: 'system',
          message: '🔌 Connexion établie avec le serveur',
          timestamp: new Date().toISOString()
        });
      }
    });

    socket.on('disconnect', () => {
      console.log('🔌 WebSocket déconnecté');
      setIsConnected(false);
    });

    // Écouter les messages du dialogue
    socket.on('agent-dialogue', (data) => {
      // ⚠️ Traiter les messages seulement si le composant est actif
      if (!isActive) {
        console.log('📭 Message ignoré (composant inactif):', data.type);
        return;
      }

      console.log('📨 Message dialogue reçu:', data);
      addMessage(data);

      // Mettre à jour la tentative courante
      if (data.tentative) {
        setCurrentTentative(data.tentative);
      }

      // Capturer le verdict final du Manager IA (succès ou acceptable)
      if (data.type === 'manager_success' || data.type === 'manager_acceptable') {
        setFinalVerdict({
          type: data.type,
          score: data.score,
          feedback: data.feedback,
          statistiques: data.statistiques,
          violations: data.violations || [],
          suggestions: data.suggestions || [],
          tentative: data.tentative
        });
      }

      // Si dialogue terminé avec succès ou échec
      if (data.type === 'dialogue_success' || data.type === 'dialogue_failure') {
        if (onComplete) {
          onComplete(data.type === 'dialogue_success');
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []); // ✅ Se connecter au montage, se déconnecter au démontage (pas de dépendance isActive)

  const addMessage = (msg) => {
    setMessages((prev) => [...prev, { ...msg, id: Date.now() + Math.random() }]);
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'deterministe_start':
      case 'deterministe_success':
      case 'deterministe_reoptimisation':
        return <BotIcon sx={{ color: '#4caf50' }} />;
      case 'deterministe_error':
        return <ErrorIcon sx={{ color: '#f44336' }} />;
      case 'planificateur_start':
      case 'planificateur_success':
        return <BotIcon sx={{ color: '#4caf50' }} />;
      case 'planificateur_error':
        return <ErrorIcon sx={{ color: '#f44336' }} />;
      case 'manager_start':
      case 'manager_success':
        return <PersonIcon sx={{ color: '#2196f3' }} />;
      case 'manager_reject':
        return <ErrorIcon sx={{ color: '#ff9800' }} />;
      case 'dialogue_success':
      case 'dialogue_partial_success':
        return <SuccessIcon sx={{ color: '#4caf50' }} />;
      case 'dialogue_failure':
        return <ErrorIcon sx={{ color: '#f44336' }} />;
      default:
        return <InfoIcon sx={{ color: '#9e9e9e' }} />;
    }
  };

  const getMessageColor = (type) => {
    if (type.startsWith('deterministe')) return '#e8f5e9'; // Vert pour déterministe
    if (type.startsWith('planificateur')) return '#e8f5e9'; // Vert pour planificateur
    if (type.startsWith('manager')) return '#e3f2fd'; // Bleu pour manager
    if (type === 'dialogue_success' || type === 'dialogue_partial_success') return '#e8f5e9';
    if (type === 'dialogue_failure') return '#ffebee';
    return '#f5f5f5';
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  // Ne rien afficher si isVisible est false (mais le WebSocket reste connecté)
  if (!isVisible) {
    return null;
  }

  return (
    <Paper elevation={3} sx={{ mt: 2, p: 2, maxHeight: '500px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* En-tête */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🎯🧠 Système Hybride : Déterministe + Manager IA
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip
            label={isConnected ? '🟢 Connecté' : '🔴 Déconnecté'}
            size="small"
            color={isConnected ? 'success' : 'error'}
            variant="outlined"
          />
          {currentTentative > 0 && (
            <Chip
              label={`Tentative ${currentTentative}`}
              size="small"
              color="primary"
            />
          )}
        </Box>
      </Box>

      {/* Barre de progression */}
      {isActive && currentTentative > 0 && currentTentative < 10 && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={(currentTentative / 10) * 100} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {currentTentative} / 10 tentatives
          </Typography>
        </Box>
      )}

      {/* Liste des messages */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: '350px' }}>
        <List>
          {messages.map((msg) => (
            <ListItem
              key={msg.id}
              alignItems="flex-start"
              sx={{
                bgcolor: getMessageColor(msg.type),
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <ListItemAvatar>
                <Avatar>{getMessageIcon(msg.type)}</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {msg.message}
                    </Typography>
                    {msg.tentative && (
                      <Chip label={`#${msg.tentative}`} size="small" />
                    )}
                    {msg.duree && (
                      <Chip label={formatDuration(msg.duree)} size="small" color="info" />
                    )}
                    {msg.score !== undefined && msg.score !== null && (
                      <Chip
                        label={`Score: ${msg.score.toFixed(1)}%`}
                        size="small"
                        color={msg.score >= 80 ? 'success' : msg.score >= 50 ? 'warning' : 'error'}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <>
                    {/* Raisonnement du planificateur */}
                    {msg.raisonnement && (
                      <Collapse in={true}>
                        <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: 'italic' }}>
                          💭 {msg.raisonnement}
                        </Typography>
                      </Collapse>
                    )}

                    {/* Feedback du manager */}
                    {msg.feedback && (
                      <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                        📝 {msg.feedback}
                      </Typography>
                    )}

                    {/* Violations */}
                    {msg.violations && msg.violations.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          ⚠️ Violations ({msg.violations.length}) :
                        </Typography>
                        {msg.violations.slice(0, 3).map((v, i) => (
                          <Typography key={i} variant="caption" component="div" sx={{ ml: 2 }}>
                            • {typeof v === 'string' ? v : (v.description || JSON.stringify(v))}
                          </Typography>
                        ))}
                        {msg.violations.length > 3 && (
                          <Typography variant="caption" component="div" sx={{ ml: 2 }}>
                            ... et {msg.violations.length - 3} autre(s)
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Suggestions */}
                    {msg.suggestions && msg.suggestions.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          💡 Suggestions :
                        </Typography>
                        {msg.suggestions.slice(0, 2).map((s, i) => (
                          <Typography key={i} variant="caption" component="div" sx={{ ml: 2 }}>
                            • {typeof s === 'string' ? s : (s.description || JSON.stringify(s))}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {/* Aperçu de la proposition */}
                    {msg.proposition && (
                      <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                        📊 {msg.proposition.nbAffectations} affectations générées
                      </Typography>
                    )}

                    {/* Timestamp */}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {new Date(msg.timestamp).toLocaleTimeString('fr-FR')}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          ))}
          <div ref={messagesEndRef} />
        </List>
      </Box>

      {/* Verdict Final du Manager IA */}
      {finalVerdict && (
        <Paper 
          elevation={6} 
          sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: finalVerdict.type === 'manager_success' ? '#e8f5e9' : '#fff3e0',
            border: '2px solid',
            borderColor: finalVerdict.type === 'manager_success' ? '#4caf50' : '#ff9800'
          }}
        >
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {finalVerdict.type === 'manager_success' ? '✅' : '⚠️'} Verdict Final du Manager IA
            {finalVerdict.score !== undefined && finalVerdict.score !== null && (
              <Chip 
                label={`${finalVerdict.score.toFixed(1)}%`} 
                color={finalVerdict.type === 'manager_success' ? 'success' : 'warning'}
                sx={{ fontWeight: 'bold' }}
              />
            )}
          </Typography>

          {/* Statistiques de planification */}
          {finalVerdict.statistiques && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                📊 Résumé de la planification :
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, ml: 2 }}>
                {finalVerdict.statistiques.couverture && (
                  <Typography variant="caption">
                    ✅ Couverture : {finalVerdict.statistiques.couverture.valide ? '100%' : 'Insuffisante'}
                  </Typography>
                )}
                {finalVerdict.statistiques.repos && (
                  <Typography variant="caption">
                    {finalVerdict.statistiques.repos.valide ? '✅' : '❌'} Repos 14j : {finalVerdict.statistiques.repos.valide ? 'Respecté' : 'Non respecté'}
                  </Typography>
                )}
                {finalVerdict.statistiques.equite && (
                  <Typography variant="caption">
                    {finalVerdict.statistiques.equite.valide ? '✅' : '⚠️'} Équité : Écart de {finalVerdict.statistiques.equite.ecart || 0} astreinte(s)
                  </Typography>
                )}
                {finalVerdict.statistiques.absences && (
                  <Typography variant="caption">
                    ✅ Absences : Prises en compte
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Points positifs */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
              ✅ Points positifs :
            </Typography>
            <Box sx={{ ml: 2 }}>
              <Typography variant="caption" component="div">
                • Couverture 100% garantie sur toutes les compétences
              </Typography>
              <Typography variant="caption" component="div">
                • Repos de 14 jours strictement respecté
              </Typography>
              <Typography variant="caption" component="div">
                • Toutes les absences déclarées ont été prises en compte
              </Typography>
              {finalVerdict.violations.length === 0 && (
                <Typography variant="caption" component="div">
                  • Aucune violation des contraintes strictes
                </Typography>
              )}
            </Box>
          </Box>

          {/* Points à améliorer */}
          {(finalVerdict.suggestions.length > 0 || finalVerdict.type === 'manager_acceptable') && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'warning.main' }}>
                💡 Points à améliorer :
              </Typography>
              <Box sx={{ ml: 2 }}>
                {finalVerdict.type === 'manager_acceptable' && finalVerdict.statistiques?.equite?.ecart > 1 && (
                  <>
                    <Typography variant="caption" component="div">
                      • Équité sous-optimale : écart de {finalVerdict.statistiques.equite.ecart} astreintes ({finalVerdict.statistiques.equite.min}-{finalVerdict.statistiques.equite.max}) (idéal : 1)
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ fontStyle: 'italic', ml: 1, mt: 0.5, color: 'text.secondary' }}>
                      ℹ️ Explication : Sur une période de {finalVerdict.statistiques?.duree || '9 semaines'}, il est difficile d'obtenir une équité parfaite.
                      {finalVerdict.statistiques.equite.ecart === 2 && ' Un écart de 2 astreintes est acceptable et commun sur une courte période.'}
                      {finalVerdict.statistiques.equite.ecart > 2 && ' Un écart de ' + finalVerdict.statistiques.equite.ecart + ' astreintes nécessiterait de replanifier certaines périodes.'}
                    </Typography>
                  </>
                )}
                {finalVerdict.suggestions.slice(0, 3).map((s, i) => (
                  <Typography key={i} variant="caption" component="div">
                    • {typeof s === 'string' ? s : (s.description || JSON.stringify(s))}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {/* Conclusion */}
          <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {finalVerdict.type === 'manager_success' 
                ? '🎉 Planning validé ! Toutes les contraintes sont respectées de manière optimale.' 
                : '✅ Planning accepté. Toutes les contraintes strictes sont respectées, mais l\'équité pourrait être améliorée dans une prochaine itération.'}
            </Typography>
          </Box>
        </Paper>
      )}
    </Paper>
  );
};

export default AgentDialogueChat;

